import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class ScaleService {
  private readonly basePlanningSheetWidth = 2180; // Base width for 30 days
  private readonly planningSheetPadding = 96;
  private planningSheetWidth: number; // Dynamic width based on the month days

  constructor() {
    this.planningSheetWidth = this.basePlanningSheetWidth; // Initialize with base width
  }

  /**
   * Returns the number of days in a given month and year.
   * @param month - The month (1 = January, 12 = December).
   * @param year - The year (e.g., 2024).
   * @returns The number of days in the given month.
   */
  private getDaysInMonth(month: number, year: number): number {
    return new Date(year, month, 0).getDate(); // Returns the last date of the month, which gives the total days
  }

  private setPlanningSheetWidthProperty(): void {
    document.documentElement.style.setProperty('--planning-sheet-width', `${this.planningSheetWidth}px`);
  }

  /**
   * Adjusts the planning sheet width based on the number of days in the month.
   * @param month - The month (1 = January, 12 = December).
   * @param year - The year (e.g., 2024).
   */
  public updatePlanningSheetWidth(month: number, year: number): void {
    const daysInMonth = this.getDaysInMonth(month, year);

    // Adjust planning sheet width based on days in the month
    this.planningSheetWidth = this.basePlanningSheetWidth + this.getWidthAdjustment(daysInMonth);

    // Update both scale and dimension properties
    this.updateScale(window.innerWidth, window.innerHeight);
    this.setPlanningSheetWidthProperty();
  }

  /**
   * Adjusts the width in pixels based on the number of days in the month.
   * @param daysInMonth - The number of days in the month.
   * @returns The width adjustment in pixels.
   */
  private getWidthAdjustment(daysInMonth: number): number {
    const emToPx = (em: number) => em * 16; // Convert em to pixels assuming 1em = 16px

    switch (daysInMonth) {
      case 28:
        return -emToPx(6);  // Subtract 6em for 28 days
      case 29:
        return -emToPx(3);  // Subtract 3em for 29 days
      case 31:
        return emToPx(3);   // Add 3em for 31 days
      default:
        return 0;           // No adjustment for 30 days
    }
  }

  /**
   * Calculates the scaling factor based on the current width.
   * @param currentWidth - The current viewport or container width.
   * @returns The calculated scaling factor.
   */
  private calculateScaleFactor(currentWidth: number): number {
    const usableWidth = currentWidth - this.planningSheetPadding;
    const percentageDifference = (this.planningSheetWidth - usableWidth) / usableWidth;
    return 1 / (1 + percentageDifference);
  }

  /**
   * Sets the scaling factor as a CSS variable based on the current width.
   * @param currentWidth - The current viewport width.
   */
  public updateScale(currentWidth: number, currentHeigh: number): void {
    const scaleFactor = this.calculateScaleFactor(currentWidth);
    document.documentElement.style.setProperty('--scale-factor', `${scaleFactor}`);
    const heightAdjustment = 324; // The amount to subtract from viewport height
    const newHeight = currentHeigh - heightAdjustment;
    const addedScaleHeight = newHeight * scaleFactor 
    const scaledHeight = newHeight + addedScaleHeight;
     // Set the scaled height based on the original height and scale factor
     document.documentElement.style.setProperty('--scaled-timeline-height', `${scaledHeight}px`);
  }
}